/**
 * Credential fields ported from the classic Connectors forms.
 * Builds the same JSON (or raw token) the existing POST/PUT handlers expect.
 * Secrets are never returned from presentCredential — key hints only.
 */

import { keyHint } from './catalogue.js';

export function normalizeGcpPrivateKey(raw) {
  return String(raw || '').trim().replace(/\\n/g, '\n');
}

function pem(raw) {
  return String(raw || '').trim().replace(/\\n/g, '\n');
}

export const SCHEMAS = {
  mixpanel: {
    intro: 'Mixpanel generates the service account username and secret when you create the account. Copy both immediately.',
    fields: [
      {
        key: 'username', label: 'Service Account Username', type: 'text',
        placeholder: 'e.g. myproject.abc123.mp-service-account',
        help: 'Not something you choose — Mixpanel generates this automatically when you create the service account, and shows it to you at that point.',
      },
      {
        key: 'secret', label: 'Service Account Secret', type: 'password',
        placeholder: 'Shown once, at creation time',
        help: 'Project Settings → Service Accounts → Create Service Account. Copy both values immediately — the secret is not shown again.',
      },
      {
        key: 'project_id', label: 'Project ID', type: 'text',
        placeholder: 'e.g. 4058505',
        help: 'Project Settings → Overview → Project ID (the number in mixpanel.com/project/…). Required — service accounts can span multiple projects.',
      },
      {
        key: 'region', label: 'Data residency', type: 'select',
        options: [
          { value: 'us', label: 'United States (mixpanel.com)' },
          { value: 'eu', label: 'European Union (eu.mixpanel.com)' },
          { value: 'in', label: 'India (in.mixpanel.com)' },
        ],
        help: 'Must match your project URL. Example: https://mixpanel.com/project/4058505 → United States. Wrong region often looks like a plan/402 error.',
      },
    ],
    build: (v) => JSON.stringify({
      serviceAccount: { username: v.username, secret: v.secret },
      project_id: v.project_id,
      region: v.region || 'us',
    }),
  },
  posthog: {
    intro: 'Create a personal API key with Query Read (and project read). Copy it once — PostHog will not show it again.',
    fields: [
      {
        key: 'personal_api_key', label: 'Personal API key', type: 'password',
        placeholder: 'phx_…',
        help: 'Settings → User → Personal API keys. Choose only read scopes (Query Read + project read). Optionally scope the key to this one project.',
      },
      {
        key: 'project_id', label: 'Project ID', type: 'text',
        placeholder: 'e.g. 12345',
        help: 'Project settings → Project ID (numeric).',
      },
      {
        key: 'host', label: 'Host', type: 'select',
        options: [
          { value: 'us', label: 'United States (us.posthog.com)' },
          { value: 'eu', label: 'European Union (eu.posthog.com)' },
          { value: 'self-hosted', label: 'Self-hosted' },
        ],
        help: 'Cloud region, or Self-hosted for your own instance. Do not use *.i.posthog.com (ingestion only).',
      },
      {
        key: 'host_url', label: 'Self-hosted URL', type: 'text', optional: true,
        placeholder: 'https://posthog.example.com',
        help: 'Required when Host is Self-hosted. Must be https and a public address (private/loopback hosts are rejected).',
      },
    ],
    build: (v) => {
      const host = v.host || 'us';
      if (host === 'self-hosted' && !String(v.host_url || '').trim()) {
        throw new Error('Self-hosted URL is required');
      }
      const out = {
        personal_api_key: v.personal_api_key,
        project_id: v.project_id,
        host,
      };
      if (host === 'self-hosted' || v.host_url) out.host_url = v.host_url;
      return JSON.stringify(out);
    },
  },
  appsflyer: {
    fields: [
      { key: 'app_id', label: 'App ID', type: 'text', placeholder: 'e.g. id123456789 or com.example.app' },
      {
        key: 'api_token', label: 'API Token (V2)', type: 'password',
        placeholder: 'A JWT from AppsFlyer Security Center',
        help: 'Account menu (top-right) → Security center → AppsFlyer API tokens → Manage → Create Token → type "API". Shown once — copy it immediately. Account admin only, and account-wide (same token for every app you connect).',
      },
    ],
    build: (v) => JSON.stringify({ app_id: v.app_id, api_token: v.api_token }),
  },
  applovin: {
    fields: [
      {
        key: 'report_key', label: 'Report Key', type: 'password',
        placeholder: 'AppLovin dashboard → account (top-right) → Keys → Report Key',
        help: 'One key covers every app and campaign on the account. It reads advertiser (user-acquisition) reporting, not publisher revenue.',
      },
    ],
    build: (v) => JSON.stringify({ report_key: v.report_key }),
  },
  appfigures: {
    fields: [
      {
        key: 'personal_access_token', label: 'Personal Access Token', type: 'password',
        placeholder: 'AppFigures → Developers → Keys → Create Personal Access Token',
        help: 'Generate under appfigures.com/developers/keys. Shown once — copy it immediately. A 403 means replace this PAT (and check API client scopes / account billing) — recheck will keep failing until a working token is saved.',
      },
      {
        key: 'product_id', label: 'Product ID (optional)', type: 'text', optional: true,
        placeholder: 'e.g. 123456789',
        help: 'AppFigures numeric product ID. If set, sales/revenue/ratings/reviews stay scoped to that one app.',
      },
    ],
    build: (v) => {
      const out = { personal_access_token: v.personal_access_token };
      if (v.product_id) out.product_id = v.product_id;
      return JSON.stringify(out);
    },
  },
  'amazon-reports': {
    fields: [
      { key: 'client_id', label: 'LWA Client ID', type: 'text', placeholder: 'Amazon Developer Console → Login with Amazon → Security Profile' },
      { key: 'client_secret', label: 'LWA Client Secret', type: 'password' },
    ],
    build: (v) => JSON.stringify({ client_id: v.client_id, client_secret: v.client_secret }),
  },
  sharepoint: {
    fields: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text', placeholder: 'Microsoft Entra tenant (directory) ID' },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'App registration client ID' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
    ],
    build: (v) => JSON.stringify({ tenant_id: v.tenant_id, client_id: v.client_id, client_secret: v.client_secret }),
  },
  firebase: {
    intro: 'Paste the whole downloaded service account JSON key file below and the fields will fill in automatically — or fill them in by hand instead.',
    notice: 'BonnieOS keeps a working copy of the last 30 days of this connector’s data so answers are fast and reliable. It refreshes about every two hours. Disconnecting removes it within 7 days.',
    autofill: true,
    fields: [
      { key: 'project_id', label: 'GCP Project ID', type: 'text', placeholder: 'e.g. my-firebase-project-12345', help: 'The JSON file\'s "project_id" field.' },
      { key: 'client_email', label: 'Client Email', type: 'text', placeholder: 'name@my-firebase-project.iam.gserviceaccount.com', help: 'The JSON file\'s "client_email" field.' },
      { key: 'private_key_id', label: 'Private Key ID', type: 'text', placeholder: 'The JSON file\'s "private_key_id" field' },
      { key: 'private_key', label: 'Private Key', type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----', help: 'The JSON file\'s "private_key" field — paste it exactly, BEGIN/END lines included. Needs the BigQuery Data Viewer role.' },
      { key: 'dataset_id', label: 'BigQuery Dataset ID', type: 'text', placeholder: 'e.g. analytics_1234567890', help: 'Firebase console → Project settings → Integrations → BigQuery — the export dataset name (not in the JSON file). Required. The service account also needs BigQuery Job User on the GCP project (bigquery.jobs.create) and the BigQuery API enabled — Data Viewer alone is not enough.' },
      { key: 'crashlytics_dataset_id', label: 'Crashlytics Dataset ID', type: 'text', placeholder: 'e.g. firebase_crashlytics', optional: true, help: 'Firebase → Integrations → BigQuery → Crashlytics export dataset. Used by the bigquery_crashlytics chat tool. Leave blank if Crashlytics is not linked to BigQuery.' },
      { key: 'performance_dataset_id', label: 'Performance Dataset ID', type: 'text', placeholder: 'e.g. firebase_performance', optional: true, help: 'Firebase → Integrations → BigQuery → Performance Monitoring export dataset. Used by the bigquery_performance chat tool. Leave blank if Performance is not linked to BigQuery.' },
    ],
    build: (v) => {
      const sa = {
        type: 'service_account',
        project_id: v.project_id,
        client_email: v.client_email,
        private_key_id: v.private_key_id,
        private_key: normalizeGcpPrivateKey(v.private_key),
        dataset_id: v.dataset_id,
      };
      if (v.crashlytics_dataset_id) sa.crashlytics_dataset_id = v.crashlytics_dataset_id;
      if (v.performance_dataset_id) sa.performance_dataset_id = v.performance_dataset_id;
      return JSON.stringify(sa);
    },
  },
  'google-analytics': {
    intro: 'Paste the whole downloaded service account JSON key file below and the fields will fill in automatically — or fill them in by hand instead.',
    autofill: true,
    fields: [
      { key: 'project_id', label: 'GCP Project ID', type: 'text', placeholder: 'e.g. my-ga-project-12345', help: 'The JSON file\'s "project_id" field.' },
      { key: 'client_email', label: 'Client Email', type: 'text', placeholder: 'name@my-ga-project.iam.gserviceaccount.com', help: 'The JSON file\'s "client_email" field.' },
      { key: 'private_key_id', label: 'Private Key ID', type: 'text', placeholder: 'The JSON file\'s "private_key_id" field' },
      { key: 'private_key', label: 'Private Key', type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----', help: 'The JSON file\'s "private_key" field — paste it exactly, BEGIN/END lines included. This service account also needs Viewer access added on the GA4 property itself.' },
      { key: 'property_id', label: 'GA4 Property ID', type: 'text', placeholder: 'e.g. 123456789', help: 'GA4 → Admin → Property Settings. NOT the same as the GCP project ID above (not in the JSON file).' },
    ],
    build: (v) => JSON.stringify({
      type: 'service_account',
      project_id: v.project_id,
      client_email: v.client_email,
      private_key_id: v.private_key_id,
      private_key: normalizeGcpPrivateKey(v.private_key),
      property_id: v.property_id,
    }),
  },
  'google-play': {
    intro: 'Paste the whole downloaded service account JSON key file below and the fields will fill in automatically — or fill them in by hand instead.',
    autofill: true,
    fields: [
      { key: 'project_id', label: 'GCP Project ID', type: 'text', placeholder: 'e.g. my-play-project-12345', help: 'The JSON file\'s "project_id" field.' },
      { key: 'client_email', label: 'Client Email', type: 'text', placeholder: 'name@my-play-project.iam.gserviceaccount.com', help: 'The JSON file\'s "client_email" field — this exact address must be added to Play Console → Users and permissions, or the API will reject it.' },
      { key: 'private_key_id', label: 'Private Key ID', type: 'text', placeholder: 'The JSON file\'s "private_key_id" field' },
      { key: 'private_key', label: 'Private Key', type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----', help: 'The JSON file\'s "private_key" field — paste it exactly, BEGIN/END lines included.' },
      { key: 'package_name', label: 'Package Name', type: 'text', placeholder: 'e.g. com.example.app', help: 'Your app\'s Android package name (applicationId). NOT in the JSON file — find it in Play Console → your app, or in build.gradle. Required for every Play API call.' },
      { key: 'account_id', label: 'Account ID', type: 'text', optional: true, placeholder: '19-digit developer account ID', help: 'Play Console → Settings → Developer account → Account details → "Developer account ID" (19 digits). Only needed for financial/sales data — not for reviews or vitals. The service account must also have "View financial data" permission in Play Console → Users and permissions.' },
    ],
    build: (v) => JSON.stringify({
      type: 'service_account',
      project_id: v.project_id,
      client_email: v.client_email,
      private_key_id: v.private_key_id,
      private_key: normalizeGcpPrivateKey(v.private_key),
      package_name: v.package_name,
      account_id: v.account_id || undefined,
    }),
  },
  'app-store-connect': {
    fields: [
      { key: 'key_id', label: 'API Key ID', type: 'text', placeholder: 'e.g. A1B2C3D4E5', help: 'App Store Connect → Users and Access → Integrations → App Store Connect API → Key ID (10-character code).' },
      { key: 'issuer_id', label: 'Issuer ID', type: 'text', placeholder: 'a UUID shown at the top of the Integrations page', help: 'App Store Connect → Users and Access → Integrations → the Issuer ID at the top.' },
      { key: 'private_key', label: 'Private Key (.p8)', type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----', help: 'The .p8 file downloaded when you created the key — paste its ENTIRE contents, BEGIN/END lines included. Apple only lets you download it once.' },
      { key: 'vendor_number', label: 'Vendor Number', type: 'text', placeholder: '8-digit number', help: 'App Store Connect → Payments and Financial Reports → the 8-digit vendor number at the top. Needed for revenue/download reports.' },
    ],
    build: (v) => JSON.stringify({
      key_id: v.key_id,
      issuer_id: v.issuer_id,
      private_key: v.private_key,
      vendor_number: v.vendor_number,
    }),
  },
  'apple-search-ads': {
    fields: [
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'e.g. SEARCHADS.xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', help: 'Shown after you upload your public key under Account Settings → API.' },
      { key: 'team_id', label: 'Team ID', type: 'text', placeholder: 'Often the same SEARCHADS.… value as Client ID', help: 'Returned with Client ID / Key ID when the API client is created. Used as the JWT iss claim.' },
      { key: 'key_id', label: 'Key ID', type: 'text', placeholder: 'UUID shown with the API client', help: 'Returned when you generate the API client — used as the JWT kid header.' },
      { key: 'org_id', label: 'Organization ID', type: 'text', placeholder: 'e.g. 1234567', help: 'Required. Sent as X-AP-Context: orgId=… on every API call.' },
      { key: 'private_key', label: 'Private Key (.pem / .p8)', type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----', help: 'The private key that matches the public key you uploaded to Apple. Paste the entire PEM, BEGIN/END lines included. Grants read+write API access — keep it secret.' },
    ],
    build: (v) => JSON.stringify({
      client_id: String(v.client_id || '').trim(),
      team_id: String(v.team_id || '').trim() || String(v.client_id || '').trim(),
      key_id: String(v.key_id || '').trim(),
      org_id: String(v.org_id || '').trim(),
      private_key: pem(v.private_key),
    }),
  },
  'google-ads': {
    fields: [
      { key: 'client_email', label: 'Client Email', type: 'text', placeholder: 'name@my-project.iam.gserviceaccount.com', help: 'The service account JSON\'s "client_email" — add this exact address to your Google Ads account (Tools → Access & Security → Users).' },
      { key: 'private_key_id', label: 'Private Key ID', type: 'text', placeholder: 'The JSON\'s "private_key_id" field' },
      { key: 'private_key', label: 'Private Key', type: 'textarea', placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----', help: 'The JSON\'s "private_key" — paste it exactly, BEGIN/END lines included.' },
      { key: 'developer_token', label: 'Developer Token', type: 'password', placeholder: 'From ads.google.com/aw/apicenter', help: 'Google Ads → Tools → API Center → Developer token. Test tokens work immediately on test accounts.' },
      { key: 'customer_id', label: 'Customer ID', type: 'text', placeholder: '10-digit account ID, no dashes', help: 'The Google Ads account ID to query — top-right of the Google Ads UI. 10 digits, no dashes.' },
      { key: 'login_customer_id', label: 'Login Customer ID', type: 'text', optional: true, placeholder: 'Manager (MCC) account ID', help: 'Only for manager accounts querying a client account — set to the manager (MCC) 10-digit ID. Leave empty for direct accounts.' },
    ],
    build: (v) => JSON.stringify({
      type: 'service_account',
      client_email: v.client_email,
      private_key_id: v.private_key_id,
      private_key: normalizeGcpPrivateKey(v.private_key),
      developer_token: v.developer_token,
      customer_id: String(v.customer_id || '').replace(/-/g, ''),
      login_customer_id: String(v.login_customer_id || '').replace(/-/g, '') || undefined,
    }),
  },
  'mongo-readonly': {
    intro: 'Point Bonnie at the read-only HTTP wrapper you deploy next to Mongo. Download the wrapper, set WRAPPER_TOKEN, and paste the base URL + token here.',
    fields: [
      { key: 'base_url', label: 'Wrapper URL', type: 'text', placeholder: 'https://mongo-wrapper.internal:8788' },
      { key: 'auth_token', label: 'Wrapper token', type: 'password', placeholder: 'WRAPPER_TOKEN' },
    ],
    build: (v) => JSON.stringify({ base_url: v.base_url, auth_token: v.auth_token }),
  },
  'sql-readonly': {
    intro: 'Same shape as the MongoDB wrapper: POST /query with a Bearer token. SELECT / find only.',
    fields: [
      { key: 'base_url', label: 'Wrapper URL', type: 'text', placeholder: 'https://sql-wrapper.internal:8787' },
      { key: 'auth_token', label: 'Wrapper token', type: 'password', placeholder: 'WRAPPER_TOKEN' },
    ],
    build: (v) => JSON.stringify({ base_url: v.base_url, auth_token: v.auth_token }),
  },
};

const SECRET_TYPES = new Set(['password', 'textarea']);

export function collectCredential(slug, values, editing) {
  const schema = SCHEMAS[slug];
  const input = values || {};
  if (!schema) {
    const value = String(input.value || '').trim();
    if (!value) return editing ? { keep: true } : { error: 'Credential value is required' };
    return { value };
  }
  const required = schema.fields.filter((field) => !field.optional);
  const missing = required.filter((field) => !String(input[field.key] || '').trim());
  const any = schema.fields.some((field) => String(input[field.key] || '').trim());
  if (!any && editing) return { keep: true };
  if (missing.length) return { error: `${missing[0].label} is required` };
  try {
    return { value: schema.build(input) };
  } catch {
    return { error: 'Could not build the credential from these fields — check nothing was left blank.' };
  }
}

export function githubMeta(values, editing) {
  const repo = String((values && values.repo) || '').trim();
  const branch = String((values && values.branch) || '').trim();
  if (!repo) return { error: 'Repository is required' };
  const metadata = { repo };
  if (branch || editing) metadata.branch = branch;
  return { metadata };
}

export function repairPastedGcpJson(raw) {
  return String(raw || '').replace(
    /(-----BEGIN PRIVATE KEY-----)([\s\S]*?)(-----END PRIVATE KEY-----)/,
    (_m, begin, body, end) => begin + body.replace(/\r?\n/g, '') + end,
  );
}

const GCP_KEYS = ['project_id', 'client_email', 'private_key_id', 'private_key'];

export function gcpAutofill(raw, slug) {
  const text = String(raw || '').trim();
  if (!text) return { values: {}, message: '' };
  let parsed;
  try { parsed = JSON.parse(text); } catch {
    try { parsed = JSON.parse(repairPastedGcpJson(text)); } catch {
      return { values: {}, message: 'Not valid JSON yet — keep pasting, or fill the fields below by hand.' };
    }
  }
  const values = {};
  for (const key of GCP_KEYS) {
    if (parsed && parsed[key] != null) values[key] = String(parsed[key]);
  }
  const n = Object.keys(values).length;
  if (!n) return { values: {}, message: 'Valid JSON, but none of the expected service-account fields were found in it.' };
  const extra = slug === 'firebase' ? 'Dataset ID' : (slug === 'google-play' ? 'Package name' : 'Property ID');
  return {
    values,
    message: `Filled ${n} field${n === 1 ? '' : 's'} from this JSON. ${extra} still needs to be entered below — it isn't in the file.`,
  };
}

function leafMap(value) {
  let obj;
  try { obj = JSON.parse(String(value || '')); } catch { return null; }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const flat = {};
  const walk = (node, prefix) => {
    if (node && typeof node === 'object' && !Array.isArray(node)) {
      for (const key of Object.keys(node)) walk(node[key], prefix ? `${prefix}.${key}` : key);
      return;
    }
    flat[prefix] = node;
  };
  walk(obj, '');
  const byLeaf = {};
  for (const key of Object.keys(flat)) {
    const leaf = key.split('.').pop();
    if (!(leaf in byLeaf)) byLeaf[leaf] = flat[key];
  }
  return byLeaf;
}

export function presentCredential(slug, raw) {
  const schema = SCHEMAS[slug];
  const leaves = leafMap(raw);
  const values = {};
  const hints = {};
  if (!leaves) {
    const hint = keyHint(raw);
    return { values, hints: { value: hint || 'Current key stays hidden' } };
  }
  const fields = schema ? schema.fields : [];
  for (const field of fields) {
    if (leaves[field.key] == null || leaves[field.key] === '') continue;
    const text = String(leaves[field.key]);
    if (SECRET_TYPES.has(field.type)) hints[field.key] = keyHint(text) || 'Current key stays hidden';
    else values[field.key] = text;
  }
  return { values, hints };
}
